import { Fragment, h } from "preact";
import styles from "./VideoCard.module.scss";
import Flickity from "flickity";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
export const VideoCard = ({ product, layout, matchProductCardRatio }) => {
    var _a;
    const sliderRef = useRef(null);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    const flickityRef = useRef(null);
    const { title, image, handle, customAttributes, birdsnestTvStorefrontData } = product;
    const { modelSize, relatedProducts = [] } = birdsnestTvStorefrontData !== null && birdsnestTvStorefrontData !== void 0 ? birdsnestTvStorefrontData : {};
    const isImageObj = (img) => typeof img === "object" && "url" in img;
    const productImage = isImageObj(image) ? image.url : "";
    const fallBackMock = 'https://cdn.shopify.com/s/files/1/0647/8531/7931/files/6e5b665d3d09bcc1bc4204cf3a57ed52231de6ab.png?v=1768537454';
    const { videoUrl, videoEmbedUrl, videoThumbnailUrl } = useMemo(() => {
        var _a;
        const empty = {
            videoUrl: undefined,
            videoEmbedUrl: undefined,
            videoThumbnailUrl: undefined,
        };
        // From Birdsnest request. Fredhopper update "customAttributes?.media" to have the first item has a parsable string (JSON);
        // Add a condition to make Preact components work with both types of media (array or string)
        let list = (_a = customAttributes === null || customAttributes === void 0 ? void 0 : customAttributes.media) !== null && _a !== void 0 ? _a : [];
        if (list.length > 0 && typeof list[0] === "string" && list[0].includes("[\"")) {
            try {
                const parsed = JSON.parse(list[0]);
                list = Array.isArray(parsed) ? parsed : [];
            }
            catch (_b) {
                list = [];
            }
        }
        if (list.length === 0)
            return empty;
        const VIDEO_EXTS = new Set(["mp4", "mov", "m3u8"]);
        const IMAGE_EXTS = new Set(["jpg", "jpeg", "png"]);
        const getUrl = (item) => typeof item === "string" ? item : item === null || item === void 0 ? void 0 : item.url;
        const ext = (url) => { var _a; return ((_a = url.split("?")[0].split(".").pop()) !== null && _a !== void 0 ? _a : "").toLowerCase(); };
        const getVimeoEmbedUrl = (url) => {
            const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
            return match ? `https://player.vimeo.com/video/${match[1]}?autoplay=1&loop=1&muted=1` : undefined;
        };
        const getYoutubeEmbedUrl = (url) => {
            const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/i);
            const id = match === null || match === void 0 ? void 0 : match[1];
            return id
                ? `https://www.youtube.com/embed/${id}?autoplay=1&loop=1&playlist=${id}&mute=1&controls=1`
                : undefined;
        };
        const getEmbedUrl = (url) => { var _a; return (_a = getVimeoEmbedUrl(url)) !== null && _a !== void 0 ? _a : getYoutubeEmbedUrl(url); };
        let video;
        let embed;
        let thumbnail;
        for (const item of list) {
            const url = getUrl(item);
            if (!url)
                continue;
            const e = ext(url);
            if (!video && !embed && VIDEO_EXTS.has(e))
                video = url;
            if (!video && !embed)
                embed = getEmbedUrl(url);
            if (!thumbnail && IMAGE_EXTS.has(e))
                thumbnail = url;
            if ((video || embed) && thumbnail)
                break;
        }
        return { videoUrl: video, videoEmbedUrl: embed, videoThumbnailUrl: thumbnail };
    }, [customAttributes === null || customAttributes === void 0 ? void 0 : customAttributes.media]);
    const showCarousel = relatedProducts.length > 1;
    useEffect(() => {
        if (!showCarousel || !sliderRef.current)
            return;
        flickityRef.current = new Flickity(sliderRef.current, {
            cellAlign: "left",
            contain: true,
            prevNextButtons: false,
            pageDots: false,
            groupCells: 1,
            wrapAround: true,
        });
        return () => {
            var _a;
            (_a = flickityRef.current) === null || _a === void 0 ? void 0 : _a.destroy();
        };
    }, [showCarousel]);
    const next = () => {
        var _a;
        (_a = flickityRef.current) === null || _a === void 0 ? void 0 : _a.next();
    };
    const prev = () => {
        var _a;
        (_a = flickityRef.current) === null || _a === void 0 ? void 0 : _a.previous();
    };
    const handlePlayClick = () => {
        setIsVideoPlaying(!isVideoPlaying);
    };
    return (h("div", { class: `${styles.videoCard} ${matchProductCardRatio ? styles.matchProductRatio : ''}` },
        h("div", { class: styles.videoCardWrapper },
            h("div", { class: styles.videoImageWrapper, onClick: !isVideoPlaying ? () => setIsVideoPlaying(true) : undefined },
                !isVideoPlaying && h(Fragment, null,
                    h("div", { class: styles.videoCardImages },
                        h("img", { src: videoThumbnailUrl || productImage || fallBackMock, alt: title })),
                    h("button", { class: styles.iconVideo, type: "button", onClick: () => setIsVideoPlaying(true) },
                        h("svg", { width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                            h("path", { d: "M12 1.99992C14.6522 1.99992 17.1958 3.05352 19.071 4.92892C20.9464 6.80424 22 9.34792 22 11.9999C22 14.6519 20.9464 17.1957 19.071 19.0709C17.1957 20.9463 14.652 21.9999 12 21.9999C9.348 21.9999 6.8042 20.9463 4.929 19.0709C3.0536 17.1956 2 14.6519 2 11.9999C2.00297 9.34872 3.0575 6.80672 4.9322 4.93212C6.80696 3.05744 9.3488 2.00292 12 1.99992ZM12 0C8.8174 0 5.7652 1.2643 3.5146 3.5146C1.2642 5.765 0 8.8174 0 12C0 15.1826 1.2643 18.2348 3.5146 20.4854C5.765 22.7358 8.8174 24 12 24C15.1826 24 18.2348 22.7357 20.4854 20.4854C22.7358 18.235 24 15.1826 24 12C24 8.8174 22.7357 5.7652 20.4854 3.5146C18.235 1.2642 15.1826 0 12 0ZM9 17V7.0002L18 12.146L9 17Z", fill: "white" })))),
                isVideoPlaying && (videoEmbedUrl ? (h("iframe", { src: videoEmbedUrl, title: title, frameBorder: "0", allow: "autoplay; fullscreen; picture-in-picture", allowFullScreen: true })) : (h("video", { autoPlay: true, loop: true, controls: true, muted: true, src: videoUrl })))),
            h("div", { class: styles.videoCardContent },
                h("div", { class: styles.videoCardContentWrapper },
                    h("p", { class: styles.videoType }, `${((_a = customAttributes === null || customAttributes === void 0 ? void 0 : customAttributes.custom_birdsnest_tv) === null || _a === void 0 ? void 0 : _a[0]) || 'All'}`),
                    h("a", { href: `/products/${handle}`, class: styles.videoCardTitle }, title),
                    modelSize && h("p", { class: styles.videoSize },
                        "Sizes Featured: ",
                        modelSize))),
            showCarousel && (h("div", { class: styles.carouselSection },
                h("div", { ref: sliderRef, className: styles.wrapperCarousel }, relatedProducts.map((item) => {
                    var _a, _b, _c, _d, _e, _f;
                    return (h("div", { key: item.id, className: styles.carouselCell },
                        h("a", { class: styles.carouselImage, href: `/products/${item.handle}`, title: item.title },
                            h("img", { loading: "lazy", src: (_c = (_b = (_a = item.productFrontImage) === null || _a === void 0 ? void 0 : _a.reference) === null || _b === void 0 ? void 0 : _b.image) === null || _c === void 0 ? void 0 : _c.url, alt: (_f = (_e = (_d = item.productFrontImage) === null || _d === void 0 ? void 0 : _d.reference) === null || _e === void 0 ? void 0 : _e.alt) !== null && _f !== void 0 ? _f : item.title }))));
                })),
                h("span", { class: `${styles.prev} ${styles.arrow}`, onClick: prev },
                    h("svg", { width: "7", height: "13", viewBox: "0 0 7 13", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                        h("path", { d: "M0.170878 6.82945L6.00422 12.6626C6.232 12.8905 6.60136 12.8905 6.82918 12.6626C7.05695 12.4348 7.05695 12.0655 6.82914 11.8377L1.40829 6.41692L6.82918 0.995913C7.05695 0.768101 7.05695 0.398773 6.82914 0.170961C6.71527 0.0570555 6.56598 0.000122051 6.41668 0.000122045C6.26739 0.000122038 6.11809 0.0570554 6.00419 0.171L0.170879 6.0045C0.0614449 6.11389 9.76388e-08 6.26226 8.41145e-08 6.41696C7.05902e-08 6.57166 0.0614448 6.72006 0.170878 6.82945Z", fill: "#475360" }))),
                h("span", { class: `${styles.next} ${styles.arrow}`, onClick: next, style: { marginLeft: 10 } },
                    h("svg", { width: "7", height: "13", viewBox: "0 0 7 13", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                        h("path", { d: "M6.82912 6.00404L0.995777 0.170854C0.768004 -0.0569578 0.398636 -0.0569578 0.170825 0.170893C-0.056948 0.398704 -0.056948 0.768033 0.170864 0.995844L5.59171 6.41658L0.170825 11.8376C-0.056948 12.0654 -0.056948 12.4347 0.170864 12.6625C0.28473 12.7764 0.434025 12.8334 0.58332 12.8334C0.732615 12.8334 0.88191 12.7764 0.995815 12.6625L6.82912 6.829C6.93856 6.7196 7 6.57124 7 6.41654C7 6.26184 6.93856 6.11344 6.82912 6.00404Z", fill: "#475360" }))))))));
};
